import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  getCollectionComponentById,
  addComponentToCollectionComponent,
  removeComponentFromCollectionComponent,
  reOrderComponentsInCollectionComponent,
  getCollectionComponentLookups,
  setCollectionComponentDynamicZone,
  editCollectionComponent,
  getCollectionComponentLayoutLookups,
  getCollectionComponentBodyLookups,
  getCollectionComponentsStandAloneLookups,
  getCollectionComponentDatatable,
  createCollectionComponentByTypeId,
  cloneCollectionComponent,
  getCollectionComponentByIdMultilingual,
  createCollectionComponentByTypeIdMultilingual,
  editCollectionComponentMultilingual,
} from './thunks';
import { CollectionComponentsState } from './types';

export const name = 'CollectionComponents';

const initialState: CollectionComponentsState = {
  details: null,
  detailsHistory: [],
  detailsStatus: 'idle',
  addComponentToCollectionComponent: {
    status: 'idle',
  },
  removeComponentFromCollectionComponent: {
    status: 'idle',
  },
  reOrderComponentsInCollectionComponent: {
    status: 'idle',
  },
  collectionComponentsDatatable: {
    data: null,
    status: 'idle',
  },
  collectionComponentLookups: {
    data: [],
    status: 'idle',
  },
  collectionComponentStandAloneLookups: {
    data: [],
    status: 'idle',
  },
  collectionComponentLayoutLookups: {
    data: [],
    status: 'idle',
  },
  collectionComponentBodyLookups: {
    data: [],
    status: 'idle',
  },
  setCollectionComponentDynamicZone: {
    status: 'idle',
  },
  editCollectionComponent: {
    data: null,
    status: 'idle',
  },
  createCollectionComponentByTypeId: {
    data: null,
    status: 'idle',
  },
  cloneCollectionComponent: {
    data: null,
    status: 'idle',
  },
  byIdMultilingual: {
    data: null,
    status: 'idle',
  },
  createMultilingual: {
    data: null,
    status: 'idle',
  },
  editMultilingual: {
    data: null,
    status: 'idle',
  },
};

export const getCollectionComponentByIdAsync = createAsyncThunk(
  name + '/ getCollectionComponentById',
  getCollectionComponentById
);

export const editCollectionComponentAsync = createAsyncThunk(
  name + '/ editCollectionComponent',
  editCollectionComponent
);

export const createCollectionComponentByTypeIdAsync = createAsyncThunk(
  name + '/ createCollectionComponentByTypeId',
  createCollectionComponentByTypeId
);

export const addComponentToCollectionComponentAsync = createAsyncThunk(
  name + '/ addComponentToCollectionComponent',
  addComponentToCollectionComponent
);

export const removeComponentFromCollectionComponentAsync = createAsyncThunk(
  name + '/ removeComponentFromCollectionComponent',
  removeComponentFromCollectionComponent
);

export const reOrderComponentsInCollectionComponentAsync = createAsyncThunk(
  name + '/ reOrderComponentsInCollectionComponent',
  reOrderComponentsInCollectionComponent
);

export const getCollectionComponentDatatableAsync = createAsyncThunk(
  name + '/ getCollectionComponentDatatable',
  getCollectionComponentDatatable
);

export const getCollectionComponentLookupsAsync = createAsyncThunk(
  name + '/ getCollectionComponentLookups',
  getCollectionComponentLookups
);

export const getCollectionComponentsStandAloneLookupsAsync = createAsyncThunk(
  name + '/ getCollectionComponentsStandAloneLookups',
  getCollectionComponentsStandAloneLookups
);

export const getCollectionComponentLayoutLookupsAsync = createAsyncThunk(
  name + '/ getCollectionComponentLayoutLookups',
  getCollectionComponentLayoutLookups
);

export const getCollectionComponentBodyLookupsAsync = createAsyncThunk(
  name + '/ getCollectionComponentBodyLookups',
  getCollectionComponentBodyLookups
);

export const setCollectionComponentDynamicZoneAsync = createAsyncThunk(
  name + '/ setCollectionComponentDynamicZone',
  setCollectionComponentDynamicZone
);

export const cloneCollectionComponentAsync = createAsyncThunk(
  name + '/ cloneCollectionComponent',
  cloneCollectionComponent
);

export const getCollectionComponentByIdMultilingualAsync = createAsyncThunk(
  name + '/ getCollectionComponentByIdMultilingual',
  getCollectionComponentByIdMultilingual
);

export const createCollectionComponentByTypeIdMultilingualAsync =
  createAsyncThunk(
    name + '/ createCollectionComponentByTypeIdMultilingual',
    createCollectionComponentByTypeIdMultilingual
  );

export const editCollectionComponentMultilingualAsync = createAsyncThunk(
  name + '/ editCollectionComponentMultilingual',
  editCollectionComponentMultilingual
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearCollectionComponentsById: (state) => {
      state.details = null;
    },
    clearCollectionComponentByIdMultilingual: (state) => {
      state.byIdMultilingual = {
        data: null,
        status: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCollectionComponentByIdAsync.pending, (state) => {
        state.detailsStatus = 'loading';
      })
      .addCase(getCollectionComponentByIdAsync.fulfilled, (state, action) => {
        state.detailsStatus = 'idle';
        state.details = action.payload;
        if (
          state.detailsHistory.find((d) => d.id === action.payload.id) ===
          undefined
        )
          state.detailsHistory.push(action.payload);
      })
      .addCase(getCollectionComponentByIdAsync.rejected, (state) => {
        state.detailsStatus = 'failed';
      })
      .addCase(addComponentToCollectionComponentAsync.pending, (state) => {
        state.addComponentToCollectionComponent.status = 'loading';
      })
      .addCase(addComponentToCollectionComponentAsync.fulfilled, (state) => {
        state.addComponentToCollectionComponent.status = 'idle';
      })
      .addCase(addComponentToCollectionComponentAsync.rejected, (state) => {
        state.addComponentToCollectionComponent.status = 'failed';
      })
      .addCase(removeComponentFromCollectionComponentAsync.pending, (state) => {
        state.removeComponentFromCollectionComponent.status = 'loading';
      })
      .addCase(
        removeComponentFromCollectionComponentAsync.fulfilled,
        (state) => {
          state.removeComponentFromCollectionComponent.status = 'idle';
        }
      )
      .addCase(
        removeComponentFromCollectionComponentAsync.rejected,
        (state) => {
          state.removeComponentFromCollectionComponent.status = 'failed';
        }
      )
      .addCase(reOrderComponentsInCollectionComponentAsync.pending, (state) => {
        state.reOrderComponentsInCollectionComponent.status = 'loading';
      })
      .addCase(
        reOrderComponentsInCollectionComponentAsync.fulfilled,
        (state) => {
          state.reOrderComponentsInCollectionComponent.status = 'idle';
        }
      )
      .addCase(
        reOrderComponentsInCollectionComponentAsync.rejected,
        (state) => {
          state.reOrderComponentsInCollectionComponent.status = 'failed';
        }
      )
      .addCase(getCollectionComponentDatatableAsync.pending, (state) => {
        state.collectionComponentsDatatable.status = 'loading';
      })
      .addCase(
        getCollectionComponentDatatableAsync.fulfilled,
        (state, action) => {
          state.collectionComponentsDatatable = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getCollectionComponentDatatableAsync.rejected, (state) => {
        state.collectionComponentsDatatable.status = 'failed';
      })
      .addCase(getCollectionComponentLookupsAsync.pending, (state) => {
        state.collectionComponentLookups.status = 'loading';
      })
      .addCase(
        getCollectionComponentLookupsAsync.fulfilled,
        (state, action) => {
          state.collectionComponentLookups = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getCollectionComponentLookupsAsync.rejected, (state) => {
        state.collectionComponentLookups.status = 'failed';
      })
      .addCase(getCollectionComponentLayoutLookupsAsync.pending, (state) => {
        state.collectionComponentLayoutLookups.status = 'loading';
      })
      .addCase(
        getCollectionComponentLayoutLookupsAsync.fulfilled,
        (state, action) => {
          state.collectionComponentLayoutLookups = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getCollectionComponentLayoutLookupsAsync.rejected, (state) => {
        state.collectionComponentLayoutLookups.status = 'failed';
      })
      .addCase(
        getCollectionComponentsStandAloneLookupsAsync.pending,
        (state) => {
          state.collectionComponentStandAloneLookups.status = 'loading';
        }
      )
      .addCase(
        getCollectionComponentsStandAloneLookupsAsync.fulfilled,
        (state, action) => {
          state.collectionComponentStandAloneLookups = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(
        getCollectionComponentsStandAloneLookupsAsync.rejected,
        (state) => {
          state.collectionComponentStandAloneLookups.status = 'failed';
        }
      )
      .addCase(getCollectionComponentBodyLookupsAsync.pending, (state) => {
        state.collectionComponentBodyLookups.status = 'loading';
      })
      .addCase(
        getCollectionComponentBodyLookupsAsync.fulfilled,
        (state, action) => {
          state.collectionComponentBodyLookups = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getCollectionComponentBodyLookupsAsync.rejected, (state) => {
        state.collectionComponentBodyLookups.status = 'failed';
      })
      .addCase(setCollectionComponentDynamicZoneAsync.pending, (state) => {
        state.setCollectionComponentDynamicZone.status = 'loading';
      })
      .addCase(setCollectionComponentDynamicZoneAsync.fulfilled, (state) => {
        state.setCollectionComponentDynamicZone.status = 'idle';
      })
      .addCase(setCollectionComponentDynamicZoneAsync.rejected, (state) => {
        state.setCollectionComponentDynamicZone.status = 'failed';
      })
      .addCase(editCollectionComponentAsync.pending, (state) => {
        state.editCollectionComponent.status = 'loading';
      })
      .addCase(editCollectionComponentAsync.fulfilled, (state, action) => {
        state.editCollectionComponent = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(editCollectionComponentAsync.rejected, (state) => {
        state.editCollectionComponent.status = 'failed';
      })
      .addCase(createCollectionComponentByTypeIdAsync.pending, (state) => {
        state.createCollectionComponentByTypeId.status = 'loading';
      })
      .addCase(
        createCollectionComponentByTypeIdAsync.fulfilled,
        (state, action) => {
          state.createCollectionComponentByTypeId = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(createCollectionComponentByTypeIdAsync.rejected, (state) => {
        state.createCollectionComponentByTypeId.status = 'failed';
      })
      .addCase(cloneCollectionComponentAsync.pending, (state) => {
        state.cloneCollectionComponent.status = 'loading';
      })
      .addCase(cloneCollectionComponentAsync.fulfilled, (state, action) => {
        state.cloneCollectionComponent = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(cloneCollectionComponentAsync.rejected, (state) => {
        state.cloneCollectionComponent.status = 'failed';
      })
      .addCase(getCollectionComponentByIdMultilingualAsync.pending, (state) => {
        state.byIdMultilingual.status = 'loading';
      })
      .addCase(
        getCollectionComponentByIdMultilingualAsync.fulfilled,
        (state, action) => {
          state.byIdMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(
        getCollectionComponentByIdMultilingualAsync.rejected,
        (state) => {
          state.byIdMultilingual.status = 'failed';
        }
      )
      .addCase(
        createCollectionComponentByTypeIdMultilingualAsync.pending,
        (state) => {
          state.createMultilingual.status = 'loading';
        }
      )
      .addCase(
        createCollectionComponentByTypeIdMultilingualAsync.fulfilled,
        (state, action) => {
          state.createMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(
        createCollectionComponentByTypeIdMultilingualAsync.rejected,
        (state) => {
          state.createMultilingual.status = 'failed';
        }
      )
      .addCase(editCollectionComponentMultilingualAsync.pending, (state) => {
        state.editMultilingual.status = 'loading';
      })
      .addCase(
        editCollectionComponentMultilingualAsync.fulfilled,
        (state, action) => {
          state.editMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(editCollectionComponentMultilingualAsync.rejected, (state) => {
        state.editMultilingual.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
